import React, { useEffect, useState } from 'react';

import {
  FilterMenu,
  SelectMenuButton,
  stateChangeTypes,
} from '@reckon-web/select-menu';
import { Flex } from '@reckon-web/flex';
import { Text } from '@reckon-web/text';
import { NotificationBadge } from '@reckon-web/badge';

import { stringCompare } from '../../utils/sort';

const LabelWithCount = (props: { label: string; count: number }) => {
  const { label, count } = props;
  return (
    <Flex alignItems="center" gap="small">
      <Text size="small" weight="medium" leading="tighter" color="muted">
        {label}
      </Text>
      {count > 0 && <NotificationBadge value={count} tone="informative" />}
    </Flex>
  );
};

export type Option<T> = { label: string; value: T; render?: React.ReactNode };

export const MultiFilterMenu = <T extends string>({
  label,
  options,
  values,
  onInputChanged,
  isLoading,
  setValues,
  disabled,
  moveSelectionsToTop = true,
  loadOptions,
  testId,
}: {
  label: string;
  options: Option<T>[];
  values: T[];
  onInputChanged?: (inputValue?: string) => void;
  isLoading?: boolean;
  setValues: (values: T[]) => void;
  disabled?: boolean;
  moveSelectionsToTop?: boolean;
  /** called whenever there's a value in the values array we haven't seen */
  loadOptions?: (keys: T[]) => Promise<Option<T>[]>;
  testId?: string;
}) => {
  // const [isLoading, setIsLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  // const [filteredOptions, setFilteredOptions] = useState(options);

  const hasCustomLoad = Boolean(onInputChanged);

  // this will hold the items we want visible in the box
  const [displayedItems, setDisplayedItems] = useState<Option<T>[]>([]);

  // very important that this starts off as an empty string. This makes it a controlled prop
  const [inputValue, setInputValue] = useState('');

  // store everything we've ever seen. This is so we can find selected items even if they're not in currently displayed list
  const [allSeenItemsCache, addToAllSeen] = useOptionCache<T>();

  const doSearch = async (inputValue: string) => {
    // remember this for later, we need it if the options change externally and we have to re-filter
    setInputValue(inputValue);
    if (onInputChanged) {
      // nothing to do. Just let the parent know that the input has changed and move on
      // it'll give us back the matching items by setting the options property
      onInputChanged(inputValue);
      return;
    }

    // no custom loader. Time for us to do our own filtering
    const matchingItems = defaultGetOptions(inputValue);
    // no need to put these into the everything we've seen, we'll be doing that elsewhere when we first get the items
    // if we're open, just glue 'em to screen? Don't augment them, just put them on screen?
    if (isOpen) {
      setDisplayedItems(matchingItems);
    } else {
      // we're closed. Now what? Is this where we make sure everything selected is at the top? Feels like it is
      rebuildDisplayedItems(matchingItems);
    }
  };

  // In terms of ordering the items - we want the selected items to stay put while the dropdown is open
  // including if they type to search. STAY.
  // As soon as it closes, we want the selected items at the top
  // Johan was of the opinion that, if we can, deselecting an item should return it to the bottom quickly
  // I don't think this would work, not without some MASSIVE messing around
  const rebuildDisplayedItems = async (
    newItems?: Option<T>[],
    overrideValues?: T[]
  ) => {
    // need to make this whole behaviour optional
    // status dropdowns will never need this sorting or putting selecteds at the top
    if (!moveSelectionsToTop) {
      if (hasCustomLoad) {
        // just display whatever they give us
        setDisplayedItems(newItems || options);
      } else {
        // display 20 of whatever they have given us
        setDisplayedItems((newItems || options).slice(0, 20));
      }
      return;
    }
    // this will be called from close, or from doSearch when closed. We must be closed. CLOSED!
    // we can ignore the search term here is what I'm trying to say
    const newDisplayedItems = [];
    // making a set because we need to refer to it later on to make sure we only display options once, not once in the selected and once in the unselected
    const selectedSet = new Set(overrideValues || values);
    const itemsToLoad: T[] = [];
    for (const selected of selectedSet) {
      const item = allSeenItemsCache.get(selected);
      if (item) {
        newDisplayedItems.push(item);
      } else {
        itemsToLoad.push(selected);
      }
    }
    // we have things in our selection we have never seen before
    if (itemsToLoad.length > 0 && loadOptions) {
      const loadedItems = await loadOptions(itemsToLoad);
      addToAllSeen(loadedItems);
      for (const loadedItem of loadedItems) {
        newDisplayedItems.push(loadedItem);
      }
    } else if (itemsToLoad.length > 0 && !loadOptions) {
      console.warn(
        'MultiFilterMenu.rebuildDisplayedItems: items need loading but there is no loadOptions',
        itemsToLoad
      );
    }
    // The selected items will not be in any particular order. Best we can do is alpha
    // this could be improved by adding a sortKey to the items later
    newDisplayedItems.sort((a, b) => stringCompare(a.label, b.label));

    // If there's no custom loader, we only want to show max 20 at once, plus their selections
    // so, take the first 20 + selection length from the array
    // This might end up with more than we need, but it won't be less than we need
    // ie: If there's 5 selections, but none of them are in the options we'll be using, we'll have 25 to choose from, but we'll only display 20 of them
    // If there IS a custom loader, display all the selections, plus whatever they've given us. But not too many or perf will suck. 100 seems fair
    const maxLength = hasCustomLoad ? 100 : 20 + newDisplayedItems.length;
    const itemsSource = (newItems || options).slice(0, maxLength);
    for (const item of itemsSource) {
      if (
        !selectedSet.has(item.value) &&
        newDisplayedItems.length < maxLength
      ) {
        newDisplayedItems.push(item);
      }
    }

    // finally... update what we're using
    setDisplayedItems(newDisplayedItems);
  };

  const optionsChanged = () => {
    // remember the options we've seen. Do this regardless of whether we have a custom search or not
    // it'll eat memory if we don't have a custom search and we do start with a list of thousands though
    // but we have to do this if the options can change AFTER they've made selections, and stay changed when they clear the search
    // specifically, users. Select the Inactive option and all the inactive users go away (this would be easier to just drop that feature...)
    // Actually. We dropped that feature. Not sure if this is still needed
    addToAllSeen(options);

    // if we are not open... I reckon just re-build the displayed items holus bolus
    // because, if we're not open, there is no search term, so everything is valid
    if (!isOpen) {
      rebuildDisplayedItems();
    } else {
      // we're open
      if (hasCustomLoad) {
        // just bang them onscreen? Seems pointless, fair chance we already have these exact options on screen
        // but, as far as we know, options have changed, so options shall be displayed
        setDisplayedItems(options);
      } else {
        // run the default search, and put those results on screen
        const matchingItems = defaultGetOptions(inputValue);
        setDisplayedItems(matchingItems);
      }
    }
  };

  const valuesChanged = () => {
    // new values have come in from the outside world
    setLocalValues(values);
    if (!isOpen) {
      // don't rebuild while we are open
      rebuildDisplayedItems();
    }
  };

  useEffect(() => {
    // I think we only need to do this if we don't have a custom search
    // if we have a custom search, WE are in control of the options
    // basically... if there's a custom search, I don't think we should be able to pass options at all
    // it should be one or the other
    // rather, we should do an eager load of the custom search in a useEffect that runs on mount
    // and use those as the base options
    optionsChanged();
    // yes, I know optionsChanged should be a dep here. But it is not (and cannot easily) be stable
    // so it's getting rebuilt every time around the render so it'll never be doing a closure around a dud variable
    // even if it was a useCallback, it'd still not be stable enough, we ONLY want to do this when the options change
    // actually... *is* it going to use the correct one, or will it have a closure around a dud function??
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options]);

  const [localValues, setLocalValues] = useState<T[]>(values);

  useEffect(() => {
    valuesChanged();
    // yes, I know valuesChanged should be a dep here. But it is not (and cannot easily) be stable
    // so it's getting rebuilt every time around the render so it'll never be doing a closure around a dud variable
    // even if it was a useCallback, it'd still not be stable enough, we ONLY want to do this when the options change
    // actually... *is* it going to use the correct one, or will it have a closure around a dud function??
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values]);

  const defaultGetOptions = (inputValue?: string) =>
    (inputValue
      ? options.filter((option) =>
          option.label.toLowerCase().includes(inputValue.toLowerCase())
        )
      : options
    ).slice(0, 20);
  return (
    // Type
    // { trigger: ({ triggerProps, hasValue }: { isOpen: boolean; hasValue: boolean; triggerProps: ButtonHTMLAttributes<HTMLButtonElement>; })
    //   => Element; options: Option<...>[]; ... 6 more ...; isLoading: boolean | undefined; }
    // is not assignable to type
    // UnionToIntersection<Option<T> extends { value: string | number; }
    //   ? { itemToValue?: ((option: { value: string | number; } & Option<T>) => string | number) | undefined; }
    //   : { itemToValue: (option: Option<...>) => string | number; }>
    // @ts-ignore // FIXME
    <FilterMenu
      data-testid={testId}
      trigger={({ triggerProps, hasValue }) => (
        <SelectMenuButton
          onClear={() => {
            // update our local collection too, so that the ui is snappy
            setLocalValues([]);
            // settimeout to allow the ui to render before we tell the parent
            setTimeout(() => {
              // reset the selection
              setValues([]);
            }, 0);
            // and rebuild what we are displaying, being VERY explicit about there being no values
            // values aren't stored here, so even though we've said "make the values empty"
            // they won't have made it to the parent and back into here by the time this next line executes
            rebuildDisplayedItems(undefined, []);
          }}
          hasValue={hasValue}
          disabled={disabled}
          {...triggerProps}
        >
          <LabelWithCount label={label} count={localValues.length} />
        </SelectMenuButton>
      )}
      options={displayedItems}
      inputValue={inputValue}
      onInputChange={async (inputValue, { type }) => {
        if (
          type !== stateChangeTypes.InputChange &&
          type !== stateChangeTypes.FunctionSetInputValue
        ) {
          return;
        }
        doSearch(inputValue || '');
      }}
      // doco says this is GUARANTEED to have an isOpen property, but typescript disagrees
      // https://github.com/downshift-js/downshift/blob/master/src/hooks/useCombobox/README.md#onisopenchange
      onIsOpenChange={(changes) => {
        if (changes.isOpen !== undefined) {
          setIsOpen(changes.isOpen);
          if (!changes.isOpen) {
            if (inputValue) {
              // there's something in there. Clear it out
              doSearch('');
            } else {
              rebuildDisplayedItems();
            }
          }
        }
      }}
      onChange={(option) => {
        const newValues = localValues.includes(option.value)
          ? localValues.filter((value) => value !== option.value)
          : [...localValues, option.value];
        setLocalValues(newValues);
        // settimeout to allow the ui to render before we tell the parent
        setTimeout(() => {
          setValues(newValues);
        }, 0);
      }}
      value={(displayedItems || []).filter((option) =>
        localValues.includes(option.value)
      )}
      itemRenderer={(option) => option.render ?? option.label}
      isLoading={isLoading}
    />
  );
};

const useOptionCache = <T extends string>() => {
  const [cache, setCache] = useState<Map<string, Option<T>>>(new Map());
  const addToCache = (items: Option<T>[]) => {
    setCache((current) => {
      const updated = new Map(current);
      for (const item of items) {
        if (!updated.has(item.value)) {
          updated.set(item.value, item);
        }
      }
      return updated;
    });
  };
  return [cache, addToCache] as const;
};
